import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
    Sizes,
    TooltipAnchor,
    TOOLTIP_BOTTOM_LOCATION,
    TOOLTIP_END_ALIGNMENT,
} from '@ratehub/base-ui';

import TRAVEL_FEATURES from '../definitions/TravelFeatures';
import TRAVEL_FEATURE_OPTIONS from '../definitions/TravelFeatureOptions';


function CardFeaturesTable({ featureDescriptions, isShowingAllFeatures }) {
    // collect the relevant features, in order, from the card’s featureDescription array, while
    // adding property featureOptionsValue which will be used to get the text from TRAVEL_FEATURE_OPTIONS
    const orderedFeatures = FEATURE_ORDER.map(featureKey => {
        // see if the ordered feature is in the featureDescriptions array
        const cardFeature = featureDescriptions.find(feature => feature.featureAPIKey === featureKey);

        // present or not, create a label and description for the array, but also note if the above lookup succeeded
        return {
            featureMessageDescriptor: TRAVEL_FEATURE_OPTIONS
                .find(option => option.value === TRAVEL_FEATURES[featureKey]).label,

            description: cardFeature?.description?.trim()
                ? cardFeature.description.trim()
                // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                : cardFeature
                    ? (
                        <FormattedMessage
                            id="edb.credit-card.features.included"
                            defaultMessage="Included"
                        />
                    )
                    : (
                        <FormattedMessage
                            id="edb.credit-card.features.not-included"
                            defaultMessage="Not included"
                        />
                    ),

            featureAPIKey: featureKey,
            tooltip: (
                <FormattedMessage
                    {...FEATURE_TOOLTIP_MESSAGES[featureKey]}
                />
            ),

            foundInFeatureDescriptions: !!cardFeature,
        };
    }).filter(feature => Boolean(isShowingAllFeatures || feature.foundInFeatureDescriptions));

    return (
        <table
            className="card-features-table rh-table padding-small no-borders zebra rh-table-text-xs rh-width-100p rh-mb-1_5"
            data-test-name="card-features-table"
        >
            <thead>
                <tr>
                    <th className="rh-text-align-left">
                        <FormattedMessage
                            id="edb.credit-card.features.table.perks"
                            defaultMessage="Perks"
                        />
                    </th>
                    {/* the second column is able to be styled by CardLayoutFooter */}
                    <th className="card-features-table-details-column rh-text-align-left">
                        <FormattedMessage
                            id="edb.credit-card.features.table.details"
                            defaultMessage="Details"
                        />
                    </th>
                    <th className="rh-text-align-left">
                        {' '}
                    </th>
                </tr>
            </thead>

            <tbody>
                <For
                    each="feature"
                    of={orderedFeatures}
                >
                    <tr key={feature.featureAPIKey}>
                        <td data-test-name="feature-label">
                            <FormattedMessage
                                {...feature.featureMessageDescriptor}
                            />
                        </td>
                        <td data-test-name="feature-description">
                            {feature.description}
                        </td>
                        <td>
                            <TooltipAnchor
                                message={feature.tooltip}
                                data-name={feature.featureAPIKey}
                                buttonSize={Sizes.ICONS['XS']}
                                tooltipWidth={'24em'}
                                nubbinAlignment={TOOLTIP_END_ALIGNMENT}
                                tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                                className="rh-text-align-center"
                            />
                        </td>
                    </tr>
                </For>
            </tbody>
        </table>
    );
}

const cardFeaturesShape = PropTypes.shape({
    featureAPIKey: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
});

CardFeaturesTable.propTypes = {
    featureDescriptions: PropTypes.oneOfType([
        PropTypes.arrayOf(cardFeaturesShape).isRequired,
        PropTypes.objectOf(cardFeaturesShape).isRequired,
    ]),
    isShowingAllFeatures: PropTypes.bool,
};


// keys must correspond to the keys in the TRAVEL_FEATURES object
// …and this is the order we prefer for the CardFeaturesTable
const FEATURE_TOOLTIP_MESSAGES = defineMessages({
    'LOUNGE_ACCESS': {
        id: 'edb.credit-card.tooltip.lounge_access',
        defaultMessage: 'You’ll gain complimentary passes to airport lounges. The number of passes and lounges you have access to will vary.',
    },
    'NO_FOREX': {
        id: 'edb.credit-card.tooltip.no_forex',
        defaultMessage: 'A surcharge that credit card holders pay for purchases made in a foreign currency (typically 2.5%).',
    },
    'TRAVEL_ACCIDENT': {
        id: 'edb.credit-card.tooltip.travel_accident',
        defaultMessage: 'If you suffer a loss as a result of an accidental bodily injury sustained while occupying a common carrier, you are eligible for this coverage.',
    },
    'TRAVEL_EMERGENCY': {
        id: 'edb.credit-card.tooltip.travel_emergency',
        defaultMessage: 'If you require urgent medical care while on a trip outside of your home province, you will be covered for any eligible medical expenses incurred.',
    },
    'CAR_RENTAL': {
        id: 'edb.credit-card.tooltip.car_rental',
        defaultMessage: 'Credit card rental car insurance usually covers theft, loss and damage to your rental car in the case of an accidental collision.',
    },
    'TRIP_CANCELLATION': {
        id: 'edb.credit-card.tooltip.trip_cancellation',
        defaultMessage: 'If you have to cancel your trip before it’s even started, your prepaid travel costs will be covered up to a maximum amount if the cause of cancellation is eligible for the insurance.',
    },
    'TRIP_INTERRUPTION': {
        id: 'edb.credit-card.tooltip.trip_interruption',
        defaultMessage: 'If your trip has to be cut short or is delayed after the scheduled departure date, you will be covered up to a maximum amount for an eligible cause of interruption or delay.',
    },
    'FLIGHT_DELAY': {
        id: 'edb.credit-card.tooltip.flight_delay',
        defaultMessage: 'If your flight departure is delayed by a certain number of hours, you’ll be reimbursed a certain amount for necessary and reasonable expenses.',
    },
    'BAGGAGE_LOSS': {
        id: 'edb.credit-card.tooltip.baggage_loss',
        defaultMessage: 'If your checked bag cannot be located by a common carrier, you’ll be reimbursed for a portion of the replacement cost of lost personal property.',
    },
    'BAGGAGE_DELAY': {
        id: 'edb.credit-card.tooltip.baggage_delay',
        defaultMessage: 'If your checked bag is not delivered within a specified time upon your arrival, you’ll be reimbursed for the cost to replace essential items.',
    },
    'HOTEL_MOTEL_BURGLARY': {
        id: 'edb.credit-card.tooltip.hotel_motel_burglary',
        defaultMessage: 'Provides protection, while you’re checked in, for your personal belongings when left unattended in your room up to a certain dollar amount.',
    },
    'MOBILE_INSURANCE': {
        id: 'edb.credit-card.tooltip.mobile_insurance',
        defaultMessage: 'Reimburses a portion of your cell phone in the event it’s damaged or lost. Device coverage is limited to a certain period of time after purchasing with your credit card.',
    },
    'PURCHASE_ASSURANCE': {
        id: 'edb.credit-card.tooltip.purchase_assurance',
        defaultMessage: 'Automatically protects new purchases by insuring them for 90 days from the date of purchase in the event of loss, theft or damage.',
    },
    'EXTENDED_WARRANTY': {
        id: 'edb.credit-card.tooltip.extended_warranty',
        defaultMessage: 'Either doubles the length of the item’s manufacturer’s warranty coverage or extends it by 1 year, whichever is less.',
    },
    'PRICE_PROTECTION': {
        id: 'edb.credit-card.tooltip.price_protection',
        defaultMessage: 'If, within a certain amount of time of purchasing an item, its price drops or you find an identical item offered for a lower retail price than what you paid, you will be reimbursed for the difference.',
    },
});

const FEATURE_ORDER = Object.keys(FEATURE_TOOLTIP_MESSAGES);

export default CardFeaturesTable;
